import React from 'react';
import { cn } from '@/utils/cn';
import { prefersReducedMotion } from '@/utils/accessibility';

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  text?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
  'aria-label'?: string;
}

export const Loading: React.FC<LoadingProps> = ({
  size = 'md',
  className,
  text = 'Loading...',
  variant = 'spinner',
  'aria-label': ariaLabel,
}) => {
  const reducedMotion = prefersReducedMotion();

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const renderSpinner = () => (
    <div
      className={cn(
        'rounded-full border-2 border-gray-600 border-t-primary-500',
        sizeClasses[size],
        !reducedMotion && 'animate-spin'
      )}
      role="status"
      aria-label={ariaLabel || text}
    />
  );

  const renderDots = () => (
    <div
      className="flex space-x-1"
      role="status"
      aria-label={ariaLabel || text}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'bg-primary-500 rounded-full',
            size === 'sm' ? 'w-1 h-1' : size === 'md' ? 'w-2 h-2' : 'w-3 h-3',
            !reducedMotion && 'animate-bounce'
          )}
          style={{
            animationDelay: !reducedMotion ? `${i * 0.1}s` : undefined,
          }}
        />
      ))}
    </div>
  );

  const renderPulse = () => (
    <div
      className={cn(
        'bg-primary-500 rounded-full',
        sizeClasses[size],
        !reducedMotion && 'animate-pulse'
      )}
      role="status"
      aria-label={ariaLabel || text}
    />
  );

  const renderLoader = () => {
    switch (variant) {
      case 'dots':
        return renderDots();
      case 'pulse':
        return renderPulse();
      default:
        return renderSpinner();
    }
  };

  return (
    <div
      className={cn('flex items-center justify-center space-x-3', className)}
      role="status"
      aria-live="polite"
    >
      {renderLoader()}
      {text && (
        <span className={cn('text-gray-400', textSizeClasses[size])}>
          {text}
        </span>
      )}
    </div>
  );
};

export const LoadingScreen: React.FC<{
  text?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
}> = ({ text = 'Loading...', variant = 'spinner' }) => {
  return (
    <div
      className="min-h-screen bg-gray-900 flex items-center justify-center"
      role="status"
      aria-live="polite"
      aria-label="Loading application"
    >
      <Loading size="lg" text={text} variant={variant} />
    </div>
  );
};

// Skeleton loading component for better perceived performance
interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = 'text',
  width,
  height,
  lines = 1,
}) => {
  const reducedMotion = prefersReducedMotion();

  const baseClasses = cn(
    'bg-gray-700',
    !reducedMotion && 'animate-pulse',
    variant === 'text' && 'h-4 rounded',
    variant === 'rectangular' && 'rounded-md',
    variant === 'circular' && 'rounded-full',
    className
  );

  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  if (variant === 'text' && lines > 1) {
    return (
      <div className="space-y-2" role="status" aria-label="Loading content">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={baseClasses}
            style={{
              ...style,
              width: i === lines - 1 ? '75%' : style.width || '100%',
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={baseClasses}
      style={style}
      role="status"
      aria-label="Loading content"
    />
  );
};
